import React, { useState } from "react";
import { Link as RouterLink, useNavigate, useLocation } from "react-router-dom";
import { useForm } from "react-hook-form";
import {
  Container,
  Box,
  Typography,
  TextField,
  Button,
  Link,
  Paper,
  InputAdornment,
  IconButton,
  CircularProgress,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  Visibility,
  VisibilityOff,
  Google as GoogleIcon,
  Security as SecurityIcon,
  GitHub as GitHubIcon,
} from "@mui/icons-material";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";
import { useThemeMode } from "../contexts/ThemeContext";
import { useTranslation } from "react-i18next";
import {
  doKeycloakLogin,
  doKeycloakGoogleLogin,
  doKeycloakGithubLogin,
} from "../services/keycloak";

const Register = () => {
  const { t } = useTranslation();
  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
  } = useForm();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser } = useAuth();
  const { mode } = useThemeMode();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // Get the destination from location state
  const from = location.state?.from?.pathname || "/";

  // Redirect if already logged in
  if (currentUser) {
    navigate(from);
    return null;
  }

  const onSubmit = async (data) => {
    if (data.password !== data.confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }

    // Set loading to true while redirecting
    setLoading(true);

    // Save form data to session storage for registration flow
    sessionStorage.setItem("registerEmail", data.email);
    sessionStorage.setItem("redirectAfterLogin", from);

    // Redirect to Keycloak registration
    doKeycloakLogin();
  };

  const handleGoogleSignIn = () => {
    // Set loading state while redirecting
    setLoading(true);

    // Save the destination URL to session storage before redirecting
    sessionStorage.setItem("redirectAfterLogin", from);

    // Use Keycloak's Google integration
    doKeycloakGoogleLogin();
  };

  const handleGithubSignIn = () => {
    // Set loading state while redirecting
    setLoading(true);

    // Save the destination URL to session storage before redirecting
    sessionStorage.setItem("redirectAfterLogin", from);

    // Use Keycloak's GitHub integration
    doKeycloakGithubLogin();
  };

  const handleKeycloakSignIn = () => {
    // Set loading state while redirecting
    setLoading(true);

    // Save the destination URL to session storage before redirecting
    sessionStorage.setItem("redirectAfterLogin", from);

    // Keycloak login will redirect to Keycloak's login page
    doKeycloakLogin();
  };

  // Social signup methods page
  const renderSocialSignupPage = () => (
    <>
      <Box sx={{ mb: 4, textAlign: "center" }}>
        <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
          <img
            src={
              mode === "light"
                ? "/Bunyan_Logo_landscape_dark.svg"
                : "/Bunyan_Logo_landscape.svg"
            }
            alt={t("auth.bunyanLogoAlt")}
            style={{ height: 50, width: "auto" }}
          />
        </Box>
        <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
          {t("auth.deployModelsEase")}
        </Typography>
      </Box>

      {/* Three outlined social buttons */}
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mb: 4 }}>
        <Button
          fullWidth
          variant="outlined"
          startIcon={<SecurityIcon />}
          onClick={handleKeycloakSignIn}
          disabled={loading}
          sx={{
            p: 0,
            height: "42px",
            borderRadius: "100px",
            "&:focus": {
              background: "var(--color-106)",
              border: "1px solid var(--color-99)",
            },
            "&:active": {
              background: "var(--color-106)",
              border: "1px solid var(--color-107)",
            },
          }}
        >
          {t("auth.continueWithSSO")}
        </Button>

        <Button
          fullWidth
          variant="outlined"
          startIcon={<GoogleIcon />}
          onClick={handleGoogleSignIn}
          disabled={loading}
          sx={{
            p: 0,
            height: "42px",
            borderRadius: "100px",
            "&:focus": {
              background: "var(--color-106)",
              border: "1px solid var(--color-99)",
            },
            "&:active": {
              background: "var(--color-106)",
              border: "1px solid var(--color-107)",
            },
          }}
        >
          {t("auth.continueWithGoogle")}
        </Button>

        <Button
          fullWidth
          variant="outlined"
          startIcon={<GitHubIcon />}
          onClick={handleGithubSignIn}
          disabled={loading}
          sx={{
            p: 0,
            height: "42px",
            borderRadius: "100px",
            "&:focus": {
              background: "var(--color-106)",
              border: "1px solid var(--color-99)",
            },
            "&:active": {
              background: "var(--color-106)",
              border: "1px solid var(--color-107)",
            },
          }}
        >
          {t("auth.continueWithGitHub")}
        </Button>
      </Box>

      <Box sx={{ textAlign: "center", mb: 3 }}>
        <Button
          fullWidth
          variant="text"
          onClick={() => setShowEmailForm(true)}
          sx={{
            textTransform: "uppercase",
            letterSpacing: 1,
            fontWeight: 500,
            borderRadius: "100px",
            p: 0,
            height: "42px",
            background: "transparent",
            color: "var(--color-99)",
            "&:hover": {
              background: "rgba(19, 211, 158, 0.08)",
              color: "var(--color-99)",
            },
            "&:focus": {
              background: "rgba(19, 211, 158, 0.12)",
              color: "var(--color-99)",
            },
            "&:active": {
              background: "rgba(19, 211, 158, 0.12)",
              color: "var(--color-99)",
            },
          }}
        >
          {t("auth.signUpWithEmail")}
        </Button>
      </Box>

      {/* Already have account button */}
      <Box sx={{ textAlign: "center" }}>
        <Button
          component={RouterLink}
          to="/login"
          fullWidth
          variant="text"
          sx={{
            textTransform: "uppercase",
            letterSpacing: 1,
            fontWeight: 500,
            borderRadius: "100px",
            p: 0,
            height: "42px",
            background: "transparent",
            color: "var(--color-99)",
            border: "none",
            "&:hover": {
              background: "rgba(19, 211, 158, 0.08)",
              color: "var(--color-99)",
            },
            "&:focus": {
              background: "rgba(19, 211, 158, 0.12)",
              color: "var(--color-99)",
            },
            "&:active": {
              background: "rgba(19, 211, 158, 0.12)",
              color: "var(--color-99)",
            },
          }}
        >
          {t("auth.alreadyHaveAccount")}
        </Button>
      </Box>
    </>
  );

  // Email signup form
  const renderEmailSignupPage = () => (
    <>
      <Box sx={{ mb: 3, textAlign: "center" }}>
        <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
          <img
            src={
              mode === "light"
                ? "/Bunyan_Logo_landscape_dark.svg"
                : "/Bunyan_Logo_landscape.svg"
            }
            alt={t("auth.bunyanLogoAlt")}
            style={{ height: 50, width: "auto" }}
          />
        </Box>
        <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
          {t("auth.deployModelsEase")}
        </Typography>
      </Box>

      <form onSubmit={handleSubmit(onSubmit)}>
        <TextField
          margin="normal"
          required
          fullWidth
          id="email"
          label={t("auth.email")}
          name="email"
          autoComplete="email"
          autoFocus
          placeholder={t("auth.emailPlaceholder")}
          {...register("email", {
            required: "Email is required",
            pattern: {
              value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
              message: "Invalid email address",
            },
          })}
          error={!!errors.email}
          helperText={errors.email?.message}
        />
        <TextField
          margin="normal"
          required
          fullWidth
          name="password"
          label={t("auth.password")}
          type={showPassword ? "text" : "password"}
          id="password"
          autoComplete="new-password"
          {...register("password", {
            required: "Password is required",
            minLength: {
              value: 6,
              message: "Password must be at least 6 characters",
            },
          })}
          error={!!errors.password}
          helperText={errors.password?.message}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  aria-label="toggle password visibility"
                  onClick={() => setShowPassword(!showPassword)}
                  edge="end"
                >
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
        <TextField
          margin="normal"
          required
          fullWidth
          name="confirmPassword"
          label={t("auth.confirmPassword")}
          type={showConfirmPassword ? "text" : "password"}
          id="confirmPassword"
          {...register("confirmPassword", {
            required: "Please confirm your password",
            validate: (value) =>
              value === watch("password") || "Passwords do not match",
          })}
          error={!!errors.confirmPassword}
          helperText={errors.confirmPassword?.message}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  aria-label="toggle confirm password visibility"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  edge="end"
                >
                  {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        <Button
          type="submit"
          fullWidth
          variant="contained"
          disabled={loading}
          sx={{ mt: 3, mb: 3, p: 0, height: "42px", borderRadius: "100px" }}
        >
          {loading ? <CircularProgress size={24} color="inherit" /> : t("auth.signUp")}
        </Button>

        <Box sx={{ textAlign: "center" }}>
          <Button
            variant="text"
            onClick={(e) => {
              e.preventDefault();
              setShowEmailForm(false);
            }}
            disabled={loading}
            sx={{
              textTransform: "uppercase",
              letterSpacing: 1,
              borderRadius: "100px",
              p: 0,
              height: "42px",
              width: "100%",
              background: "transparent",
              color: "var(--color-99)",
              fontSize: "14px",
              border: "none",
              "&:hover": {
                background: "rgba(19, 211, 158, 0.08)",
                color: "var(--color-99)",
              },
              "&:focus": {
                background: "rgba(19, 211, 158, 0.12)",
                color: "var(--color-99)",
              },
              "&:active": {
                background: "rgba(19, 211, 158, 0.12)",
                color: "var(--color-99)",
              },
            }}
          >
            {t("auth.signUpMethods")}
          </Button>
        </Box>

        <Box sx={{ textAlign: "center" }}>
          <Button
            component={RouterLink}
            to="/login"
            fullWidth
            variant="text"
            sx={{
              textTransform: "uppercase",
              letterSpacing: 1,
              fontWeight: 500,
              borderRadius: "100px",
              p: 0,
              height: "42px",
              background: "transparent",
              color: "var(--color-99)",
              border: "none",
              "&:hover": {
                background: "rgba(19, 211, 158, 0.08)",
                color: "var(--color-99)",
              },
              "&:focus": {
                background: "rgba(19, 211, 158, 0.12)",
                color: "var(--color-99)",
              },
              "&:active": {
                background: "rgba(19, 211, 158, 0.12)",
                color: "var(--color-99)",
              },
            }}
          >
            {t("auth.alreadyHaveAccount")}
          </Button>
        </Box>
      </form>
    </>
  );

  return (
    <Box
      sx={{
        minHeight: "100vh",
        minWidth: "100vw",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background:
          mode === "dark"
            ? `radial-gradient(50% 50% at 57% 115%, var(--color-102) 0%, rgba(41, 74, 77, 0.00) 100%), var(--color-34)`
            : `radial-gradient(50% 50% at 57% 115%, var(--color-21) 0%, rgba(229, 244, 242, 0.00) 100%), var(--color-108)`,
        position: "fixed",
        top: 0,
        insetInlineStart: 0,
        insetInlineEnd: 0,
        bottom: 0,
        overflow: "auto",
        "&::before": {
          content: '""',
          position: "absolute",
          insetInlineStart: "-190px",
          bottom: "-289px",
          width: "1820px",
          height: "673px",
          borderRadius: "1820px",
          opacity: 0.6,
          background:
            "radial-gradient(50% 50% at 50% 50%, var(--color-102) 0%, rgba(41, 74, 77, 0.00) 100%)",
          pointerEvents: "none",
          zIndex: 0,
        },
        "&::after": {
          content: '""',
          position: "absolute",
          insetInlineEnd: "0",
          top: "50%",
          transform: "translateY(-50%)",
          width: "clamp(400px, 40vw, 800px)",
          height: "clamp(480px, 92vh, 960px)",
          backgroundImage: "url(/svg-background-login-registration.svg)",
          backgroundRepeat: "no-repeat",
          backgroundSize: "contain",
          backgroundPosition: "center",
          opacity: mode === "dark" ? 0.7 : 0.15,
          pointerEvents: "none",
          zIndex: 0,
          "@media (max-width: 775px)": {
            display: "none",
          },
        },
      }}
    >
      <Container
        component="main"
        maxWidth="sm"
        sx={{ py: 4, position: "relative", zIndex: 1 }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Paper
            elevation={3}
            sx={{
              p: 4,
              width: isMobile ? "100%" : "80%",
              borderRadius: 2,
              background:
                mode === "light"
                  ? "rgba(255, 255, 255, 0.9)"
                  : "var(--color-109)",
              backdropFilter: "blur(10px)",
            }}
          >
            {!showEmailForm
              ? renderSocialSignupPage()
              : renderEmailSignupPage()}
          </Paper>
        </Box>
      </Container>
    </Box>
  );
};

export default Register;
